import { useEffect, useRef, useState } from "react";
import { X, MessageCircle } from "lucide-react";
import { useT } from "../../i18n/LanguageContext";
import { trackEvent } from "../../analytics";

const HOSTS = [
  { name: "Carlos", phone: "351965209781", role: "carlos", initials: "C" },
  { name: "Rute",   phone: "351965488960", role: "rute",   initials: "R" },
];

export default function WhatsAppFab() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const link = (phone) =>
    `https://wa.me/${phone}?text=${encodeURIComponent(t.whatsapp.greeting)}`;

  return (
    <div ref={ref} className="fixed bottom-5 right-5 z-50" data-testid="whatsapp-fab">
      {open && (
        <div className="mb-3 w-72 bg-white rounded-2xl shadow-2xl ring-1 ring-[#0e1a3f]/15 overflow-hidden animate-in fade-in slide-in-from-bottom-3" data-testid="whatsapp-popover">
          <div className="bg-[#075E54] text-white px-5 py-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
              <MessageCircle size={20} />
            </div>
            <div className="flex-1">
              <p className="font-semibold">{t.whatsapp.title}</p>
              <p className="text-xs text-white/85 mt-0.5">{t.whatsapp.subtitle}</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" aria-label="close" data-testid="whatsapp-close">
              <X size={18} />
            </button>
          </div>
          <div className="p-3 space-y-2 bg-[#f3ecdc]">
            {HOSTS.map((h) => (
              <a
                key={h.role}
                href={link(h.phone)}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackEvent("whatsapp_click", { host: h.role })}
                className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 hover:bg-[#075E54] hover:text-white transition group"
                data-testid={`whatsapp-${h.role}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#25D366] text-white font-display font-bold flex items-center justify-center flex-shrink-0">
                  {h.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0e1a3f] group-hover:text-white">
                    {h.role === "carlos" ? `${t.whatsapp.carlos}` : `${t.whatsapp.rute}`}
                  </p>
                  <p className="text-[11px] text-[#0e1a3f]/65 group-hover:text-white/80">+{h.phone}</p>
                </div>
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#25D366] group-hover:fill-white flex-shrink-0">
                  <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04.06C5.42.06.04 5.44.04 12.06c0 2.12.56 4.18 1.6 6L0 24l6.13-1.6a12.02 12.02 0 0 0 5.91 1.55h.01c6.62 0 12-5.38 12-12 0-3.2-1.25-6.21-3.53-8.47zM12.05 21.9a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.64.95.97-3.55-.24-.36a9.85 9.85 0 0 1-1.51-5.28C2.24 6.55 6.62 2.17 12.05 2.17c2.65 0 5.13 1.03 7 2.9a9.83 9.83 0 0 1 2.9 7c0 5.43-4.42 9.83-9.9 9.83zm5.42-7.36c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15s-.77.97-.95 1.17c-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.48 1.07 2.88 1.22 3.08c.15.2 2.11 3.22 5.1 4.51.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.3.17-1.41-.07-.12-.27-.2-.57-.34z"/>
                </svg>
              </a>
            ))}
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl whatsapp-pulse hover:scale-110 active:scale-95 transition"
        aria-label={t.whatsapp.open}
        data-testid="whatsapp-trigger"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current">
          <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04.06C5.42.06.04 5.44.04 12.06c0 2.12.56 4.18 1.6 6L0 24l6.13-1.6a12.02 12.02 0 0 0 5.91 1.55h.01c6.62 0 12-5.38 12-12 0-3.2-1.25-6.21-3.53-8.47zM12.05 21.9a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.64.95.97-3.55-.24-.36a9.85 9.85 0 0 1-1.51-5.28C2.24 6.55 6.62 2.17 12.05 2.17c2.65 0 5.13 1.03 7 2.9a9.83 9.83 0 0 1 2.9 7c0 5.43-4.42 9.83-9.9 9.83zm5.42-7.36c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15s-.77.97-.95 1.17c-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.48 1.07 2.88 1.22 3.08c.15.2 2.11 3.22 5.1 4.51.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.3.17-1.41-.07-.12-.27-.2-.57-.34z" />
        </svg>
      </button>
    </div>
  );
}
